
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { EyeFilled, EyeInvisibleFilled, InfoCircleFilled } from '@ant-design/icons-vue';
import { AxiosError } from 'axios';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength as maxLengthValidator, required } from '@vuelidate/validators';
import { api } from '@/store';

const REGEX = {
  LOWERCASE: /[a-z]/,
  UPPERCASE: /[A-Z]/,
  NUMBER: /\d/,
  // eslint-disable-next-line no-useless-escape
  SPECIAL_CHAR: /[!@#$%^&*()_+"№;?:*{}[\]'"\\|/,.<>\/?`~=-]/,
};

const MIN_LENGTH = 8;
const MAX_LENGTH = 64;

export default defineComponent({
  name: 'NewPassword',
  emits: ['success', 'goToLogin'],

  components: {
    InfoCircleFilled,
  },

  props: {
    isPinTimeOver: { type: Boolean, default: false },
    token: { type: String, required: true },
  },

  setup(props, { emit }) {
    const isRequestInProgress = ref(false);
    const errorMessage = ref('');
    const showPassword = ref(false);
    const showPasswordConfirm = ref(false);
    const password = ref('');
    const passwordConfirm = ref('');

    const validations = useVuelidate(
      {
        password: {
          required: helpers.withMessage('', required),
          maxLength: helpers.withMessage(`Максимальная длина ${MAX_LENGTH} символов`, maxLengthValidator(MAX_LENGTH)),
          lengthValid: helpers.withMessage(`Минимум ${MIN_LENGTH} символов`, (v: string) => v?.length >= MIN_LENGTH),
          lowercase: helpers.withMessage('Добавьте хотя бы одну строчную букву', (v: string) =>
            REGEX.LOWERCASE.test(v),
          ),
          uppercase: helpers.withMessage('Добавьте хотя бы одну заглавную букву', (v: string) =>
            REGEX.UPPERCASE.test(v),
          ),
          digit: helpers.withMessage('Добавьте хотя бы одну цифру', (v: string) => REGEX.NUMBER.test(v)),
          symbol: helpers.withMessage('Добавьте хотя бы один специальный символ (#, !, %, $, @ и т.д.)', (v: string) =>
            REGEX.SPECIAL_CHAR.test(v),
          ),
        },
        passwordConfirm: {
          required: helpers.withMessage('', required),
          sameAsPassword: helpers.withMessage('Пароли не совпадают', () => password.value === passwordConfirm.value),
        },
      },
      { password, passwordConfirm },
      { $lazy: true },
    );

    const ruleChecks = computed(() => ({
      lengthValid: password.value.length >= MIN_LENGTH,
      lowercase: REGEX.LOWERCASE.test(password.value),
      uppercase: REGEX.UPPERCASE.test(password.value),
      digit: REGEX.NUMBER.test(password.value),
      symbol: REGEX.SPECIAL_CHAR.test(password.value),
    }));

    const ruleStyle = (key: keyof typeof ruleChecks.value) => ({
      backgroundColor: ruleChecks.value[key] ? '#52c41a' : 'red',
    });

    watch([password, passwordConfirm], ([newPass, newConfirm], [oldPass, oldConfirm]) => {
      if (newPass !== oldPass) {
        validations.value.password.$touch();
      }

      if (newConfirm !== oldConfirm) {
        validations.value.passwordConfirm.$touch();
      }

      errorMessage.value = '';
    });

    const passwordStrength = computed(() => {
      const pwd = validations.value.password;
      const pwdVal = password.value;

      if (!pwdVal.length || !pwd.$dirty) {
        return { percent: 0, label: '', color: '#ccc', status: 'normal' };
      }

      if (pwdVal.length < MIN_LENGTH) {
        return { percent: Math.min(pwdVal.length * 10, 33), label: 'Простой', color: '#ff4d4f', status: 'exception' };
      }

      const checks = [
        ruleChecks.value.lowercase,
        ruleChecks.value.uppercase,
        ruleChecks.value.digit,
        ruleChecks.value.symbol,
      ];

      const score = checks.filter(Boolean).length;

      if (score <= 2) {
        return { percent: 33, label: 'Простой', color: '#ff4d4f', status: 'exception' };
      }
      if (score <= 3) {
        return { percent: 66, label: 'Средний', color: '#faad14', status: 'active' };
      }
      return { percent: 100, label: 'Надёжный', color: '#389e0d', status: 'success' };
    });

    onMounted(() => {
      if (password.value.length || passwordConfirm.value.length) {
        validations.value.$touch();
      }
    });

    const firstError = computed(() => {
      if (props.isPinTimeOver) {
        return 'Срок действия ссылки истек';
      }

      if (errorMessage.value.length) {
        return errorMessage.value;
      }

      const pwdErr = validations.value.password.$errors;
      if (pwdErr.length) {
        return pwdErr[0].$message;
      }

      const confirmErr = validations.value.passwordConfirm.$errors;
      if (confirmErr.length) {
        return confirmErr[0].$message;
      }

      return '';
    });

    const isFormInvalid = computed(() => {
      if (props.isPinTimeOver) {
        return true;
      }

      if (isRequestInProgress.value) {
        return true;
      }

      if (errorMessage.value.length) {
        return true;
      }

      const v = validations.value;
      return v.password.$invalid || v.passwordConfirm.$invalid || !password.value || !passwordConfirm.value;
    });

    const execute = async () => {
      isRequestInProgress.value = true;

      try {
        await api.post({ url: '/restoreSubmit', data: { token: props.token, password: password.value } });
        emit('success', password.value);
      } catch (error) {
        let errorText = 'Не удалось создать пароль';

        if (error instanceof AxiosError) {
          if (error.response?.data?.message === 'Password must not be the same as your login') {
            errorText = 'Нельзя использовать логин в качестве пароля';
          }
        }

        errorMessage.value = errorText;
      } finally {
        isRequestInProgress.value = false;
      }
    };

    return {
      password,
      passwordConfirm,
      validations,
      showPassword,
      showPasswordConfirm,
      ruleStyle,
      passwordStrength,
      EyeFilled,
      EyeInvisibleFilled,
      firstError,
      isFormInvalid,
      execute,
      isRequestInProgress,
      errorMessage,
      MIN_LENGTH,
    };
  },
});
